import { TextField, TextFieldProps } from "@mui/material";
import React, { useEffect, useState } from "react";

interface NumberTextFieldProps extends Omit<
  TextFieldProps,
  "value" | "onChange"
> {
  value: number | string;
  roundTo?: number;
  minValue?: number;
  maxValue?: number;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

const NumberTextField = ({
  value,
  onChange,
  disabled,
  roundTo,
  minValue,
  maxValue,
  ...otherProps
}: NumberTextFieldProps) => {
  const [inputValue, setInputValue] = useState<string | number>(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const formatValue = (val: string) => {
    let input = val.replace(/[^\d.]/g, "");
    const parts = input.split(".");
    if (parts.length > 2) {
      input = `${parts[0]}.${parts[1]}`;
    }
    return input;
  };

  const applyMinMax = (val: string): string => {
    if (val === "" || isNaN(Number(val))) return val;
    let num = parseFloat(val);

    if (minValue !== undefined && num < minValue) num = minValue;
    if (maxValue !== undefined && num > maxValue) num = maxValue;

    return String(num);
  };

  const roundNumber = (val: string): string => {
    if (roundTo === undefined || val === "" || isNaN(Number(val))) {
      return val;
    }
    const num = parseFloat(val);
    const factor = Math.pow(10, roundTo);
    return String(Math.round(num * factor) / factor);
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let tempValue = e.target.value;
    tempValue = roundNumber(tempValue);
    tempValue = applyMinMax(tempValue);
    setInputValue(tempValue);

    if (onChange) {
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: tempValue,
        },
      } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
      onChange(newEvent);
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const formatted = formatValue(event.target.value);
    setInputValue(formatted);

    if (onChange) {
      const newEvent = {
        ...event,
        target: {
          ...event.target,
          value: formatted,
        },
      } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
      onChange(newEvent);
    }
  };

  return (
    <TextField
      {...otherProps}
      disabled={disabled}
      value={inputValue}
      type="text"
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

export default NumberTextField;
